import type {Logger, LogLevel} from './types';
import {ConsoleLogger} from './console-logger';
import {exists} from 'shared/utils/custom-utils';
import {isError} from 'shared/utils/error-utils';
import {LOG_LEVELS} from './constants';
import {CompositeLogger} from 'core/logging/composite-logger';

export class LogService implements Logger {
    public static serviceName = 'logService';
    public static defaultLevel = LOG_LEVELS.WARN;

    private logLevel: LogLevel = LogService.defaultLevel;
    private logger = new CompositeLogger().addLogger(new ConsoleLogger());

    addLogger(logger: Logger): this {
        this.logger.addLogger(logger);
        return this;
    }

    setLoggingLevel(logLevel: LogLevel): this {
        if (logLevel < LOG_LEVELS.TRACE || logLevel > LOG_LEVELS.ERROR) {
            throw Error(`Invalid logging level: ${logLevel}. Possible values are 1..5 or are listed in LOG_LEVELS constant.`);
        }
        this.logLevel = logLevel;
        return this;
    }

    trace(...args: any[]): void {
        if (this.logLevel <= LOG_LEVELS.TRACE) {
            this.logger.trace(...this.formatArgs(args));
        }
    }

    debug(...args: any[]): void {
        if (this.logLevel <= LOG_LEVELS.DEBUG) {
            this.logger.debug(...this.formatArgs(args));
        }
    }

    info(...args: any[]): void {
        if (this.logLevel <= LOG_LEVELS.INFO) {
            this.logger.info(...this.formatArgs(args));
        }
    }

    warn(...args: any[]): void {
        if (this.logLevel <= LOG_LEVELS.WARN) {
            this.logger.warn(...this.formatArgs(args));
        }
    }

    error(...args: any[]): void {
        if (this.logLevel <= LOG_LEVELS.ERROR) {
            this.logger.error(...this.formatArgs(args));
        }
    }

    private formatArgs(args: any[]): any[] {
        return args.map((arg) => this.formatError(arg));
    }

    private formatError(arg: any) {
        if (isError(arg)) {
            if (exists(arg.stack)) {
                arg = (arg.message && arg.stack.includes(arg.message))
                    ? arg.stack
                    : `Error: ${arg.message}\n${arg.stack}`;
            }
        }
        return arg;
    }
}